import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { format, parseISO } from 'date-fns';

interface AuditLog {
  id: number;
  action: string;
  column_name: string;
  change_summary: string;
  old_value: string;
  new_value: string;
  changed_at: string;
  changed_by: {
    type: string;
    name: string;
    email: string;
  };
  request_context: {
    request_id: string;
    ip_address: string;
    user_agent: string;
  };
}

interface TimesheetLogsModalProps {
  open: boolean;
  onClose: () => void;
  timesheetId: number;
  auditLogs: AuditLog[];
}

const TimesheetLogsModal: React.FC<TimesheetLogsModalProps> = ({
  open,
  onClose,
  timesheetId,
  auditLogs,
}) => {
  const formatDateTime = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy h:mm a');
    } catch (error) {
      return dateString;
    }
  };

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'success';
      case 'update':
        return 'primary';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatColumnName = (columnName: string) => {
    return columnName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Timesheet Logs - ID: {timesheetId}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Audit trail of all changes made to this timesheet
        </Typography>
      </DialogTitle>

      <DialogContent>
        {auditLogs.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No audit logs found for this timesheet.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {auditLogs.map((log, index) => (
              <Box key={log.id}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={1}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={log.action}
                        color={getActionColor(log.action) as any}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {formatColumnName(log.column_name)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(log.changed_at)}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontStyle: 'italic' }}
                  >
                    {log.change_summary}
                  </Typography>

                  <Stack direction="row" spacing={2} mb={1}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Previous Value:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {log.old_value || 'NULL'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        New Value:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {log.new_value || 'NULL'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      p: 1,
                      backgroundColor: 'grey.50',
                      borderRadius: 0.5,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Changed by:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {log.changed_by.name} ({log.changed_by.type})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {log.changed_by.email}
                    </Typography>
                  </Box>
                </Box>
                {index < auditLogs.length - 1 && <Divider sx={{ my: 1 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimesheetLogsModal;
